(() => {
  'use strict';

  if (customElements.get('edm-offer-tracking')) return;

  const publish = (name, payload) => {
    if (!window.Shopify?.analytics?.publish) return;
    try {
      const result = window.Shopify.analytics.publish(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      // Analytics must never block the storefront interaction.
    }
  };

  class EdmOfferTracking extends HTMLElement {
    connectedCallback() {
      this.section = this.querySelector('[data-edm-offer]');
      if (!this.section || this.section.dataset.edmTrackingReady === 'true') return;
      this.section.dataset.edmTrackingReady = 'true';
      this.promotion = this.querySelector('[data-edm-offer-promotion]');
      this.topSlot = this.querySelector('[data-edm-offer-top-slot]');
      this.bottomSlot = this.querySelector('[data-edm-offer-bottom-slot]');
      this.variant = this.getVariant();
      this.applyVariant();
      publish(
        'veloura_edm_landing_view',
        this.payload({ creative_slot: 'landing_page' })
      );
      this.promotionRegion = this.querySelector('[data-edm-promotion-region]');
      this.copyButton = this.querySelector('[data-edm-copy]');
      this.clickHandler = event => this.handleClick(event);
      this.copyHandler = () => this.copyCoupon();
      this.addEventListener('click', this.clickHandler);
      this.copyButton?.addEventListener('click', this.copyHandler);
      this.observePromotion();
    }

    getVariant() {
      if (this.dataset.offerPosition === 'bottom') return 'bottom';
      const requestedVariant = new URLSearchParams(window.location.search).get('offer_position');
      return requestedVariant?.toLowerCase() === 'bottom' ? 'bottom' : 'top';
    }

    applyVariant() {
      this.dataset.offerPosition = this.variant;
      this.section.dataset.experimentVariant = this.variant;
      const destination = this.variant === 'bottom' ? this.bottomSlot : this.topSlot;
      if (destination && this.promotion && this.promotion.parentElement !== destination) {
        destination.appendChild(this.promotion);
      }
      this.promotion?.querySelectorAll('[data-edm-cta]').forEach(cta => {
        cta.dataset.creativeSlot = this.variant === 'bottom' ? 'story_offer' : 'hero_offer';
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('click', this.clickHandler);
      this.copyButton?.removeEventListener('click', this.copyHandler);
      if (this.section) delete this.section.dataset.edmTrackingReady;
    }

    payload(extra = {}) {
      return {
        promotion_id: this.section.dataset.promotionId || '',
        promotion_name: this.section.dataset.promotionName || '',
        creative_name: this.section.dataset.creativeName || '',
        landing_page_type: 'edm_welcome',
        offer_id: this.section.dataset.offerId || '',
        experiment_id: this.section.dataset.experimentId || '',
        experiment_variant: this.variant || 'top',
        ...extra,
      };
    }

    observePromotion() {
      if (!this.promotionRegion) return;
      const sendView = () => {
        if (this.section.dataset.edmPromotionViewed === 'true') return;
        this.section.dataset.edmPromotionViewed = 'true';
        publish(
          'veloura_view_promotion',
          this.payload({ creative_slot: this.variant === 'bottom' ? 'story_offer' : 'hero_offer' })
        );
      };
      if (!('IntersectionObserver' in window)) {
        sendView();
        return;
      }
      this.observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
            sendView();
            this.observer.disconnect();
          }
        },
        { threshold: [0.5] }
      );
      this.observer.observe(this.promotionRegion);
    }

    handleClick(event) {
      const cta = event.target.closest('[data-edm-cta]');
      if (!cta || !this.contains(cta)) return;
      publish(
        'veloura_select_promotion',
        this.payload({
          creative_slot: cta.dataset.creativeSlot || '',
          cta_id: cta.dataset.ctaId || '',
          destination_type: cta.dataset.destinationType || '',
        })
      );
    }

    async copyCoupon() {
      const code = this.querySelector('[data-edm-coupon-code]')?.textContent?.trim();
      if (!code || !this.copyButton) return;
      let copied = false;
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(code);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }
      if (!copied) copied = this.copyWithTextArea(code);
      if (!copied) return;

      const originalLabel = this.copyButton.dataset.copyLabel || this.copyButton.textContent;
      const copiedLabel = this.copyButton.dataset.copiedLabel || originalLabel;
      this.copyButton.textContent = copiedLabel;
      const status = this.querySelector('[data-edm-copy-status]');
      if (status) status.textContent = copiedLabel;
      publish(
        'veloura_coupon_copy',
        this.payload({
          creative_slot: this.variant === 'bottom' ? 'story_offer' : 'hero_offer',
          cta_id: 'copy_coupon',
          coupon: code,
        })
      );
      window.setTimeout(() => {
        if (!this.copyButton?.isConnected) return;
        this.copyButton.textContent = originalLabel;
        if (status) status.textContent = '';
      }, 1800);
    }

    copyWithTextArea(value) {
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      input.remove();
      return copied;
    }
  }

  customElements.define('edm-offer-tracking', EdmOfferTracking);
})();
